import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import BrandDashboardLayout from "../../components/BrandDashboardLayout";
import { toast } from "sonner";

const SPORTS = ["Football", "Basketball", "Athletics", "MMA / Combat", "Cycling", "Rugby", "Cricket", "Tennis", "Golf", "Esports", "Swimming", "Padel"];
const REGIONS = ["UK", "Europe", "Middle East", "USA", "Asia", "Africa", "Australia", "Global"];
const BUDGETS = ["£250-£1K", "£1K-£5K", "£5K-£15K", "£15K-£50K", "£50K+"];

export default function BrandProfile() {
  const [form, setForm] = useState({
    company_name: "", category: "", website: "", headquarters: "",
    budget_range: "", target_sports: [], target_regions: [], audience_focus: "", company_bio: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/brand/profile").then(({ data }) => {
      setForm(f => ({
        ...f, ...data,
        target_sports: data.target_sports || [],
        target_regions: data.target_regions || [],
      }));
    });
  }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggle = (k, v) => upd(k, form[k].includes(v) ? form[k].filter(x => x !== v) : [...form[k], v]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/brand/profile", form);
      toast.success("Brand profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <BrandDashboardLayout>
      <div className="p-6 md:p-12 max-w-3xl">
        <div className="mb-10">
          <div className="label-eyebrow">Public to athletes</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="brand-profile-title">Brand Profile</h1>
          <p className="text-ink-muted mt-2 text-sm">This is what athletes see when you start a partnership conversation. Complete profiles get 3x more replies.</p>
        </div>
        <div className="space-y-5">
          <Field label="Company / brand name"><Input value={form.company_name} onChange={v => upd("company_name", v)} testid="bp-name" /></Field>
          <Field label="Category"><Input value={form.category} onChange={v => upd("category", v)} placeholder="Apparel, Nutrition, Fintech…" testid="bp-category" /></Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Website"><Input value={form.website} onChange={v => upd("website", v)} placeholder="https://acme.com" testid="bp-website" /></Field>
            <Field label="Headquarters"><Input value={form.headquarters} onChange={v => upd("headquarters", v)} placeholder="London, UK" testid="bp-hq" /></Field>
          </div>
          <Field label="Typical campaign budget">
            <div className="flex flex-wrap gap-2">
              {BUDGETS.map(b => (
                <button key={b} type="button" onClick={() => upd("budget_range", b)} data-testid={`bp-budget-${b}`}
                  className={`px-3 py-2 border text-xs uppercase tracking-widest ${form.budget_range === b ? "border-volt text-volt" : "border-line text-ink-muted hover:text-white"}`}>{b}</button>
              ))}
            </div>
          </Field>
          <Field label="Audience focus">
            <textarea rows={3} value={form.audience_focus} onChange={e => upd("audience_focus", e.target.value)} data-testid="bp-audience"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white resize-none" />
          </Field>
          <Field label="Target sports">
            <div className="flex flex-wrap gap-2">
              {SPORTS.map(s => (
                <button key={s} type="button" onClick={() => toggle("target_sports", s)} data-testid={`bp-sport-${s}`}
                  className={`px-3 py-2 border text-xs uppercase tracking-widest ${form.target_sports.includes(s) ? "border-volt text-volt" : "border-line text-ink-muted hover:text-white"}`}>{s}</button>
              ))}
            </div>
          </Field>
          <Field label="Target regions">
            <div className="flex flex-wrap gap-2">
              {REGIONS.map(r => (
                <button key={r} type="button" onClick={() => toggle("target_regions", r)} data-testid={`bp-region-${r}`}
                  className={`px-3 py-2 border text-xs uppercase tracking-widest ${form.target_regions.includes(r) ? "border-volt text-volt" : "border-line text-ink-muted hover:text-white"}`}>{r}</button>
              ))}
            </div>
          </Field>
          <Field label="Company bio">
            <textarea rows={5} value={form.company_bio} onChange={e => upd("company_bio", e.target.value)} data-testid="bp-bio"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white resize-none" />
          </Field>
          <div className="flex justify-end">
            <button onClick={save} disabled={saving} className="btn-volt disabled:opacity-40" data-testid="bp-save-btn">
              {saving ? "Saving…" : "Save brand profile"}
            </button>
          </div>
        </div>
      </div>
    </BrandDashboardLayout>
  );
}

function Field({ label, children }) { return <div><label className="label-eyebrow block mb-2">{label}</label>{children}</div>; }
function Input({ value, onChange, placeholder = "", testid }) {
  return <input value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
    className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" />;
}
